import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  IconButton, 
  Box, 
  Typography, 
  MenuItem, 
  Menu, 
  ListItemIcon, 
  ListItemText, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Avatar,
  Paper
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import Person from "@mui/icons-material/Person";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSelector } from "react-redux";
import ClickableChip from "../../../../components/common/ClickableChip";
import { getOnboardingDashboard, updateBgvStatus, updateEmployee } from "../../../../api/employeeApi";
import { getEntities } from "../../../../api/entityApi";
import { getLocations } from "../../../../api/locationApi";
import { Work } from "@mui/icons-material";
import OnboardingDetailsModal from "./OnboardingDetailsModal";
import CustomNoResultsOverlay from "../employee-directory/components/CustomNoResultsOverlay";


// Define status options for BGV (background verification specific)
const statusOptions = [
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CLEAR', label: 'Clear' },
  { value: 'DISCREPANCY', label: 'Discrepancy' },
  { value: 'NOT_STARTED', label: 'Not Started' },
];

const filterInputStyle = {
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      borderColor: 'rgba(117, 34, 209, 0.15)',
      transition: 'all 0.2s ease-in-out',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(117, 34, 209, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#7522D1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#4F4937',
    '&.Mui-focused': {
      color: '#7522D1',
    },
  },
};

const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string?.length || 0; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const Bgv = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [entityFilter, setEntityFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState("ALL");
  
  const [entities, setEntities] = useState([]);
  const [locations, setLocations] = useState([]);
  
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [failureReason, setFailureReason] = useState("");
  const [tempStatusUpdate, setTempStatusUpdate] = useState(null);

  // Fetch Entities (Backend filters by user scope)
  useEffect(() => {
    const fetchEntitiesList = async () => {
      const res = await getEntities();
      if (res.success) setEntities(res.data);
    };
    if (isAuthenticated) fetchEntitiesList();
  }, [user, isAuthenticated]);

  // Fetch Locations when entity is selected
  useEffect(() => {
    const fetchLocationsList = async () => {
      if (entityFilter !== 'ALL') {
        const res = await getLocations({ entityId: entityFilter });
        if (res.success) setLocations(res.data);
        setLocationFilter('ALL'); // Reset location when entity changes
      } else {
        setLocations([]);
        setLocationFilter('ALL');
      }
    };
    fetchLocationsList();
  }, [entityFilter]);

  const handleStatusChange = async (employeeId, field, newStatus) => {
    // If status is Failed or Discrepancy, prompt for a reason
    if (newStatus === 'FAILED' || newStatus === 'DISCREPANCY') {
      setTempStatusUpdate({ employeeId, field, newStatus });
      setFailureReason("");
      setReasonDialogOpen(true);
      return;
    }

    await executeStatusUpdate(employeeId, field, newStatus);
  };

  const executeStatusUpdate = async (employeeId, field, newStatus, reason = null) => {
    try {
      const isFailedStatus = newStatus === 'FAILED' || newStatus === 'DISCREPANCY';
      const reasonToPersist = isFailedStatus ? reason : null;

      // Update the state to reflect the change immediately for better UX
      setEmployees(prevEmployees =>
        prevEmployees.map(emp =>
          emp.id === employeeId
            ? { ...emp, [field]: newStatus, failureReason: reasonToPersist !== null ? reasonToPersist : emp.failureReason }
            : emp
        )
      );

      // Make API call to persist the change
      await updateBgvStatus(employeeId, { field, newStatus, reason: reasonToPersist });
    } catch (error) {
      console.error(`Failed to update ${field} for employee ${employeeId}:`, error);
      fetchData(); // Revert on failure
    }
  };

  const handleReasonSubmit = () => {
    if (tempStatusUpdate) {
      executeStatusUpdate(
        tempStatusUpdate.employeeId,
        tempStatusUpdate.field,
        tempStatusUpdate.newStatus,
        failureReason
      );
    }
    setReasonDialogOpen(false);
    setTempStatusUpdate(null);
  };

  const handleMenuClick = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmployee(null);
  };

  const handleViewDetails = () => {
    setDetailsModalOpen(true);
    handleMenuClose();
  };

  const handleMoveToOfferStatus = async () => {
    if (!selectedEmployee) return;

    try {
      setLoading(true);
      // Move to Offer phase
      const response = await updateEmployee(selectedEmployee.id, {
        showInOfferStatus: true,
        showInBgv: false
      });

      if (response.success) {
        await fetchData();
      }
    } catch (error) {
      console.error('Error moving to offer status:', error);
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const openMenu = Boolean(anchorEl);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, statusFilter, entityFilter, locationFilter, employeeTypeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Only fetch employees in BGV phase with current filters
      const response = await getOnboardingDashboard({ 
        showInBgv: true,
        search: searchQuery,
        status: statusFilter,
        entityId: entityFilter,
        workLocation: locationFilter,
        employmentType: employeeTypeFilter
      });
      if (response.success) {
        const formatted = response.data.map(emp => {
          const bgv = emp.onboardingSummary?.bgv || {};
          return {
            id: emp.id,
            empNo: emp.employeeId,
            name: emp.name,
            email: emp.email,
            bgvStatus: (bgv.status || "PENDING").toUpperCase().replace(" ", "_"),
            verificationAgency: bgv.agency || "-",
            criminalCheck: (bgv.criminalCheck || "NOT_STARTED").toUpperCase().replace(" ", "_"),
            educationCheck: (bgv.educationCheck || "NOT_STARTED").toUpperCase().replace(" ", "_"),
            employmentCheck: (bgv.employmentCheck || "NOT_STARTED").toUpperCase().replace(" ", "_"),
            failureReason: bgv.failureReason || null,
            verificationDate: bgv.checkedAt || null,
          };
        });
        setEmployees(formatted);
      }
    } catch (error) {
      console.error("Error fetching BGV data:", error);
    } finally {
      setLoading(false);
    }
  };

  // No need for separate rows variable if we use employees directly
  const rows = employees;

  const columns = [
    {
      field: "empNo",
      headerName: "EMPLOYEE NO.",
      width: 150,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#7522D1" }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "NAME",
      width: 220,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const name = params.value || "";
        const initials = name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase() || "EE";
        const avatarColor = stringToColor(name);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: "0.85rem",
                fontWeight: 600,
                bgcolor: avatarColor,
                color: "#FFFFFF",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              {initials}
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#030007" }}>
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "EMAIL",
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "bgvStatus",
      headerName: "BGV STATUS",
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClickableChip
              status={params.value}
              statusOptions={statusOptions}
              allowUpdate={true}
              onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'bgvStatus', newStatus)}
            />
            {params.row.failureReason && (params.value === 'FAILED' || params.value === 'DISCREPANCY') && (
              <Tooltip title={`Reason: ${params.row.failureReason}`}>
                <ErrorOutlineIcon color="error" fontSize="small" sx={{ cursor: 'pointer' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "criminalCheck",
      headerName: "POLICE CHECK",
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClickableChip
              status={params.value}
              statusOptions={statusOptions}
              allowUpdate={true}
              onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'criminalCheck', newStatus)}
            />
            {params.row.failureReason && (params.value === 'FAILED' || params.value === 'DISCREPANCY') && (
              <Tooltip title={`Reason: ${params.row.failureReason}`}>
                <ErrorOutlineIcon color="error" fontSize="small" sx={{ cursor: 'pointer' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "educationCheck",
      headerName: "EDUCATION CHECK",
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClickableChip
              status={params.value}
              statusOptions={statusOptions}
              allowUpdate={true}
              onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'educationCheck', newStatus)}
            />
            {params.row.failureReason && (params.value === 'FAILED' || params.value === 'DISCREPANCY') && (
              <Tooltip title={`Reason: ${params.row.failureReason}`}>
                <ErrorOutlineIcon color="error" fontSize="small" sx={{ cursor: 'pointer' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "employmentCheck",
      headerName: "EMPLOYMENT CHECK",
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClickableChip
              status={params.value}
              statusOptions={statusOptions}
              allowUpdate={true}
              onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'employmentCheck', newStatus)}
            />
            {params.row.failureReason && (params.value === 'FAILED' || params.value === 'DISCREPANCY') && (
              <Tooltip title={`Reason: ${params.row.failureReason}`}>
                <ErrorOutlineIcon color="error" fontSize="small" sx={{ cursor: 'pointer' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "verificationDate",
      headerName: "VERIFICATION DATE",
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        return params.value ? new Date(params.value).toLocaleDateString() : "-";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      resizable: false,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={(event) => handleMenuClick(event, params.row)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 2,
              sx: {
                borderRadius: 2,
                minWidth: 200,
                mt: 1,
                '& .MuiMenuItem-root': {
                  py: 1.5,
                  px: 2,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleViewDetails}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(117, 34, 209, 0.08)',
                  color: '#7522D1',
                  '& .MuiListItemIcon-root': {
                    color: '#7522D1',
                  },
                },
              }}
            >
              <ListItemIcon>
                <Person sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText primary="View Details" primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
            <MenuItem onClick={handleMoveToOfferStatus}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(117, 34, 209, 0.08)',
                  color: '#7522D1',
                  '& .MuiListItemIcon-root': {
                    color: '#7522D1',
                  },
                },
              }}
            >
              <ListItemIcon>
                <Work fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Move to Offer Status" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderLeft: "6px solid #7522D1",
          padding: "20px 24px",
          marginBottom: 3,
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(117, 34, 209, 0.05)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          border: "1px solid rgba(117, 34, 209, 0.08)",
          borderLeftWidth: "6px",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#030007",
              marginBottom: 0.5,
              fontSize: "1.15rem",
              letterSpacing: "0.2px",
            }}
          >
            Background Verification (BGV)
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6E6855",
              fontSize: "0.9rem",
              fontWeight: 400,
            }}
          >
            Monitor and track real-time verification progress across criminal record checks, education credentials, and prior employment.
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "16px",
          border: "1px solid rgba(117, 34, 209, 0.08)",
          backgroundColor: "#FFF",
          boxShadow: "0 4px 20px rgba(117, 34, 209, 0.02)",
          mb: 3,
        }}
      >
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              placeholder="Search by Employee ID, Name, Email..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={filterInputStyle}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(117, 34, 209, 0.4)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {(user?.role?.hierarchyLevel === 'Level 1' || user?.role?.code === 'SUPER_ADMIN') && (
            <>
              <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                <FormControl size="small" fullWidth sx={filterInputStyle}>
                  <InputLabel>Entity</InputLabel>
                  <Select
                    value={entityFilter}
                    label="Entity"
                    onChange={(e) => setEntityFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Entities</MenuItem>
                    {entities.map(ent => (
                      <MenuItem key={ent.id || ent._id} value={ent.id || ent._id}>{ent.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                <FormControl size="small" fullWidth disabled={entityFilter === 'ALL'} sx={filterInputStyle}>
                  <InputLabel>Work Location</InputLabel>
                  <Select
                    value={locationFilter}
                    label="Work Location"
                    onChange={(e) => setLocationFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Locations</MenuItem>
                    {locations.map(loc => (
                      <MenuItem key={loc.id || loc._id} value={loc.id || loc._id}>{loc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
            <FormControl size="small" fullWidth sx={filterInputStyle}>
              <InputLabel>Employee Type</InputLabel>
              <Select
                value={employeeTypeFilter}
                label="Employee Type"
                onChange={(e) => setEmployeeTypeFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="FULL_TIME">Full Time</MenuItem>
                <MenuItem value="PART_TIME">Part Time</MenuItem>
                <MenuItem value="CONTRACT">Contract</MenuItem>
                <MenuItem value="INTERN">Intern</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
            <FormControl size="small" fullWidth sx={filterInputStyle}>
              <InputLabel>BGV Status</InputLabel>
              <Select
                value={statusFilter}
                label="BGV Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                {statusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <OnboardingDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        employeeId={selectedEmployee?.id}
      />

      <Dialog 
        open={reasonDialogOpen} 
        onClose={() => setReasonDialogOpen(false)} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Typography variant="h6" fontWeight={700} color="#030007">
            Update Failure Reason
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "#6E6855" }}>
            Please provide a reason for the <strong>{tempStatusUpdate?.newStatus}</strong> status.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason / Remarks"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
            sx={filterInputStyle}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button 
            onClick={() => setReasonDialogOpen(false)}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              color: "#4F4937",
              fontWeight: 600,
              paddingX: 2.5,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReasonSubmit}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              backgroundColor: "#7522D1",
              color: "#FFFFFF",
              fontWeight: 600,
              paddingX: 3,
              boxShadow: "0 4px 12px rgba(117, 34, 209, 0.2)",
              "&:hover": {
                backgroundColor: "#5C17A8",
                boxShadow: "0 6px 16px rgba(117, 34, 209, 0.3)",
              }
            }}
            disabled={!failureReason.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ height: 540, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableRowSelectionOnClick
          rowHeight={64}
          sortingMode="client"
          filterMode={false}
          hideFooter={false}
          slots={{
            noRowsOverlay: CustomNoResultsOverlay,
          }}
          sx={{
            border: "none",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(117, 34, 209, 0.04)",
            backgroundColor: "white",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F8F5FC",
              borderBottom: "1px solid rgba(117, 34, 209, 0.08)",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "#42374F",
                fontSize: "0.85rem",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              },
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.9rem",
              borderBottom: "1px solid rgba(117, 34, 209, 0.04)",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              alignItems: "center !important",
              transition: "all 0.2s ease",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(117, 34, 209, 0.02)",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid rgba(117, 34, 209, 0.08)",
              backgroundColor: "#F8F5FC",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Bgv;
